/**
 * ActivityStrip — messages per day for the open thread.
 *
 * The shape of a conversation over time is the fastest way to find the part
 * worth reading: bursts are visible at a glance, and clicking a bar scopes the
 * whole view to that bucket, which is how an analyst frames a range without
 * typing dates. Purely a navigation aid — it never changes what is promoted.
 */

import { type FC, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { CommsActivityBucket } from '@/services/api/comms'
import { cn } from '@/utils/cn'
import { dayLabel } from './comms-format'

/** A bucket's window, so a click can scope to exactly that bar. */
const BUCKET_MS = 24 * 3_600_000

export const ActivityStrip: FC<{
  buckets: CommsActivityBucket[]
  onPick: (from: string, to: string) => void
}> = ({ buckets, onPick }) => {
  const { t, i18n } = useTranslation()
  // Below lg (phone, or the narrow thread pane of a tablet) the bars keep a
  // tappable width — and a tap area as tall as the strip, however low the
  // bar — and the strip scrolls sideways instead; it opens on the newest day,
  // like the thread under it. From lg the strip does not scroll, so the
  // effect is a no-op there.
  const stripRef = useRef<HTMLDivElement>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-anchor when the thread's histogram changes
  useEffect(() => {
    const el = stripRef.current
    if (el) el.scrollLeft = el.scrollWidth
  }, [buckets])
  if (buckets.length < 2) return null
  const peak = Math.max(...buckets.map(b => b.n))
  return (
    <div
      ref={stripRef}
      className="scrollbar-none flex h-10 items-end gap-px border-b border-border bg-muted/20 px-3 pb-1 pt-1.5 max-lg:overflow-x-auto md:px-4"
    >
      {buckets.map(bucket => {
        const start = new Date(bucket.bucket)
        const end = new Date(start.getTime() + BUCKET_MS)
        return (
          <button
            key={bucket.bucket}
            type="button"
            title={`${dayLabel(bucket.bucket, i18n.language)} · ${t('comms.sitting.count', {
              count: bucket.n,
            })}`}
            onClick={() => onPick(start.toISOString(), end.toISOString())}
            className={cn(
              'min-w-1.5 flex-1 rounded-sm bg-primary/50 transition-colors hover:bg-primary lg:min-w-0',
              'max-lg:relative max-lg:after:absolute max-lg:after:inset-x-0 max-lg:after:bottom-0 max-lg:after:h-7',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
            )}
            // Height is data, not styling — a token class cannot express it.
            style={{ height: `${Math.max(8, (bucket.n / peak) * 100)}%` }}
          />
        )
      })}
    </div>
  )
}
